import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useExperience, phaseIndex } from '@/store/experience';

// The supplied coin footage was pre-matted offline (scripts/coin_matte.py) into a
// side-by-side video: left half = colour, right half = alpha. The shader recombines
// them so the coin floats in OUR atmosphere and can pass between our cloud layers.
const fragment = /* glsl */ `
uniform sampler2D uVideo; uniform float uOpacity; uniform vec3 uFog; uniform float uFogAmt; uniform float uBlur; uniform float uWhite;
varying vec2 vUv;
vec4 smp(vec2 uv){ return texture2D(uVideo, vec2(uv.x * 0.5, uv.y)); }
float alp(vec2 uv){ return texture2D(uVideo, vec2(0.5 + uv.x * 0.5, uv.y)).r; }
void main(){
  vec2 o = vec2(0.0, uBlur);
  vec4 c = (smp(vUv) + smp(vUv + o) + smp(vUv - o) + smp(vUv + o * 2.0) + smp(vUv - o * 2.0)) / 5.0;
  float a = (alp(vUv) + alp(vUv + o) + alp(vUv - o) + alp(vUv + o * 2.0) + alp(vUv - o * 2.0)) / 5.0;
  vec2 d = (vUv - 0.5) * vec2(2.0, 1.15);
  float feather = 1.0 - smoothstep(0.7, 1.0, length(d));
  vec3 col = c.rgb * 1.06 + vec3(1.0, 0.85, 0.6) * 0.04;
  col = mix(col, uFog, uFogAmt);
  col = mix(col, vec3(1.0), uWhite * 0.7);
  #ifdef COIN_DEBUG
  gl_FragColor = COIN_DEBUG == 1 ? vec4(1.0, 0.0, 0.0, 0.8) : COIN_DEBUG == 2 ? vec4(vec3(a), 1.0) : COIN_DEBUG == 3 ? vec4(c.rgb, 1.0) : COIN_DEBUG == 4 ? vec4(col, a) : vec4(col, a * feather);
  #else
  gl_FragColor = vec4(col, a * feather * uOpacity);
  #endif
}`;
const vertex = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

export function CoinPlane({ video }) {
  const ref = useRef();
  const frame = useExperience((s) => s.frame);
  const phase = useExperience((s) => s.phase);

  // Frames are copied through a 2D canvas: works everywhere (incl. software GL), unlike direct video upload
  const { texture, canvas, ctx } = useMemo(() => {
    const c = document.createElement('canvas');
    c.width = 1024; c.height = 910;
    const t = new THREE.CanvasTexture(c);
    t.minFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    return { texture: t, canvas: c, ctx: c.getContext('2d') };
  }, []);

  const uniforms = useMemo(() => ({
    uVideo: { value: texture }, uOpacity: { value: 0 }, uFog: { value: new THREE.Color('#8fc1ec') },
    uFogAmt: { value: 0.1 }, uBlur: { value: 0 }, uWhite: { value: 0 },
  }), [texture]);

  useEffect(() => {
    if (phaseIndex(phase) >= phaseIndex('COIN_REVEAL')) video.play().catch(() => {});
  }, [phase, video]);

  useFrame(({ camera }) => {
    const { coin } = frame;
    const depth = 8;
    // coin.x / coin.y are fractions of the visible half-extent at this depth (viewport-relative)
    const halfH = depth * Math.tan((camera.fov * Math.PI) / 360);
    const halfW = halfH * camera.aspect;
    const v = new THREE.Vector3(coin.x * halfW, coin.y * halfH, -depth).applyQuaternion(camera.quaternion).add(camera.position);
    ref.current.position.copy(v);
    ref.current.quaternion.copy(camera.quaternion);
    const h = Math.min(halfH * 1.4, 5.2) * Math.max(coin.scale, 0.0001);
    ref.current.scale.set(h * (512 / 910), h, 1);
    const u = ref.current.material.uniforms;
    u.uOpacity.value = coin.opacity;
    u.uBlur.value = coin.blur;
    u.uWhite.value = frame.fog;
    ref.current.visible = coin.opacity > 0.01;
    if (ref.current.visible && video.readyState >= 2) { ctx.drawImage(video, 0, 0, canvas.width, canvas.height); texture.needsUpdate = true; }
  });

  return (
    <mesh ref={ref} renderOrder={2} frustumCulled={false}>
      <planeGeometry args={[1, 1]} />
      <shaderMaterial vertexShader={vertex} fragmentShader={fragment} uniforms={uniforms} transparent depthWrite={false} depthTest={false} defines={window.location.search.includes('coindebug') ? { COIN_DEBUG: Number(new URLSearchParams(window.location.search).get('coindebug')) || 1 } : {}} />
    </mesh>
  );
}
